// --- Our Modules --------------------------------------------------------------------------------
import useLang from "@/hooks/useLang";
import useTranslation from "@/hooks/useTranslation";
import type { TyContract, TyReservation } from "@/types";
import { formatDate, formatMoney, pressToOpen, readLangValue } from "@/utils";
import Avatar from "@/UI/atoms/Avatar";
import Icon from "@/UI/atoms/Icon";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
type Props = {
    deal: ({ kind: "contract" } & TyContract) | ({ kind: "reservation" } & TyReservation);
    /** Off on a customer's own screen, where the person is the page. */
    hidePerson?: boolean;
    /** The whole card. Everything it is too small to say — and cancelling — is in the popup. */
    onOpen?: () => void;
};

/**
 * A contract or a hold, as one card.
 *
 * They are drawn by the same component because they are read in the same two places and answer the
 * same three questions — who, which unit, on what terms — and a person scanning a customer's page
 * should not have to learn two layouts to find out whether the flat is theirs yet.
 *
 * **Six facts, and the figure is the headline.** Who, which flat, when, what it went for, what came
 * off the asking price, and whether it still stands — that is the row, in that order, because the
 * question somebody scans a list of contracts with is "how much, and is it still on". Everything
 * else about the paper is in the popup the card opens.
 *
 * **The discount is printed, not left as arithmetic.** A sale signed under the unit's listed price
 * is the one thing on these screens that reads as a contradiction, and the only fix is to say which
 * of the two numbers is the asking price. A let has no such pair — rent is not measured against a
 * sale price — so it carries the figure alone.
 *
 * **Nothing here cancels anything.** Undoing a sale is reached from inside the popup's menu: a card
 * in a grid is pressed by the thumb that meant to open it.
 */
export default function DealCard({ deal, hidePerson, onOpen }: Props) {
    const t = useTranslation();
    const lang = useLang((state) => state.lang) ?? "ar";

    const isContract = deal.kind === "contract";
    const customer = typeof deal.customer === "object" && deal.customer ? deal.customer : null;
    const unit = typeof deal.unit === "object" && deal.unit ? deal.unit : null;
    const project = unit && typeof unit.project === "object" && unit.project ? readLangValue(unit.project.name, lang) : null;

    const title = isContract ? t.properties.offers[deal.type] : t.sales.reserve;
    const state = isContract ? t.sales.contractStatuses[deal.isLive ? "live" : "cancelled"] : t.sales.reservationStatuses[deal.status];

    /** A contract is dated by its signature, a hold by the day it runs out. */
    const date = isContract
        ? { label: t.sales.signedAt, value: deal.signedAt }
        : { label: t.sales.expiresAt, value: deal.expiresAt ?? deal.createdAt };

    const agreed = isContract ? deal.amount : deal.price;
    const asked = unit?.price ?? null;
    const isComparable = !isContract || deal.type === "sale";
    const discount = isComparable && asked && agreed && asked > agreed ? asked - agreed : null;

    return (
        <article
            className={`deal-card deal-card--${isContract ? "contract" : "reservation"}${deal.isLive ? "" : " deal-card--closed"}`}
            role={onOpen ? "button" : undefined}
            tabIndex={onOpen ? 0 : undefined}
            onClick={onOpen}
            onKeyDown={onOpen ? pressToOpen(onOpen) : undefined}
        >
            <div className="deal-card__top">
                <span className="deal-card__mark" aria-hidden="true">
                    <Icon name={isContract ? "document" : "history"} size={16} />
                </span>

                <div className="deal-card__identity">
                    <span className="deal-card__title">{title}</span>
                    {unit && (
                        <span className="deal-card__where">
                            <Icon name="properties" size={12} />
                            {project ? `${project} · ` : ""}
                            {unit.number}
                        </span>
                    )}
                </div>

                <span className="deal-card__state">{state}</span>
            </div>

            {/* Whose it is. Not a link any more — the card is one gesture, and the popup it opens
                carries the way through to the person. */}
            {!hidePerson && customer && (
                <div className="deal-card__person">
                    <Avatar name={customer.name} picture={customer.picture ?? null} size={28} rounded />
                    <span className="deal-card__person-text">
                        <span className="deal-card__name">{customer.name}</span>
                        {/* A number reads left to right in every language. */}
                        <bdi className="deal-card__phone" dir="ltr">
                            {customer.phone}
                        </bdi>
                    </span>
                </div>
            )}

            <div className="deal-card__foot">
                <span className="deal-card__money">
                    <strong>
                        <bdi dir="ltr">{agreed ? formatMoney(agreed) : t.properties.notSet}</bdi>
                        {!!agreed && <small>{t.properties.currency}</small>}
                    </strong>
                    {discount !== null && (
                        <span className="deal-card__discount">
                            {t.sales.discount} <bdi dir="ltr">{formatMoney(discount)}</bdi>
                        </span>
                    )}
                </span>

                {date.value && (
                    <span className="deal-card__when">
                        <Icon name="calendar" size={12} />
                        <small>{date.label}</small>
                        <bdi dir="ltr">{formatDate(date.value, lang)}</bdi>
                    </span>
                )}
            </div>
        </article>
    );
}
